import { Archive, ArrowLeft, ArrowRight, Download, FileSpreadsheet, FileText, FileType, LockKeyhole, PackageOpen, Presentation } from 'lucide-react';
import { Button } from '../../design-system/Button';
import type { AnalysisReport } from '../../engine/core/types';
import { formatBytes, shortHash } from '../../utilities/format';
import { HealthPanel } from './HealthPanel';
import { IssueList } from './IssueList';
import { RepairPlan } from './RepairPlan';

interface AnalysisWorkspaceProps {
  report: AnalysisReport;
  onRepair: () => void;
  onExtract: () => void;
  onReset: () => void;
}

export function AnalysisWorkspace({ report, onRepair, onExtract, onReset }: AnalysisWorkspaceProps): React.JSX.Element {
  const FileIcon = report.adapter === 'word' || report.adapter === 'opendocument' ? FileText : report.adapter === 'powerpoint' ? Presentation : report.adapter === 'pdf' ? FileType : report.adapter === 'archive' ? Archive : FileSpreadsheet;
  const word = report.word;
  const powerPoint = report.powerPoint;
  const pdf = report.pdf;
  const archive = report.archive;
  const odt = report.odt;
  return (
    <div className="analysis-workspace">
      <div className="breadcrumb"><button type="button" onClick={onReset}><ArrowLeft />New file</button><span>/</span><strong>{report.file.name}</strong></div>
      <section className="file-identity">
        <div className="file-identity__icon"><FileIcon /></div>
        <div><h1>{report.file.name}</h1><p>{report.detected.format.toUpperCase()} · {formatBytes(report.file.size)} · <span className="mono">SHA-256 {shortHash(report.file.sha256.slice(0, 24))}…</span></p></div>
      </section>
      <section className="analysis-intro">
        <div><h2>Your file may not be lost yet.</h2><p>Here is what MendByte found, what it can safely change, and what remains uncertain.</p></div>
        <div className="local-callout"><LockKeyhole /><span><strong>No upload</strong>All processing happened on your device.</span></div>
      </section>
      <div className="analysis-grid">
        <HealthPanel health={report.health} />
        <section className="analysis-panel glance-panel">
          <div className="panel-heading"><h2>At a glance</h2></div>
          {report.adapter === 'opendocument' && odt ? (
            <dl>
              <div><dt>Paragraphs</dt><dd>{odt.paragraphs.toLocaleString()}</dd></div>
              <div><dt>Words</dt><dd>{odt.words.toLocaleString()}</dd></div>
              <div><dt>Tables</dt><dd>{odt.tables}</dd></div>
              <div><dt>Images</dt><dd>{odt.images}</dd></div>
              <div><dt>Tracked changes</dt><dd>{odt.trackedChanges}</dd></div>
              <div><dt>Manifest</dt><dd>{odt.manifestStatus === 'valid' ? 'Valid' : 'Needs recovery'}</dd></div>
            </dl>
          ) : report.adapter === 'word' && word ? (
            <dl>
              <div><dt>Paragraphs</dt><dd>{word.paragraphs.toLocaleString()}</dd></div>
              <div><dt>Words</dt><dd>{word.words.toLocaleString()}</dd></div>
              <div><dt>Tables</dt><dd>{word.tables}</dd></div>
              <div><dt>Images</dt><dd>{report.counts.images}</dd></div>
              <div><dt>Tracked changes</dt><dd>{word.trackedChanges}</dd></div>
              <div><dt>VBA macros</dt><dd>{report.hasMacros ? 'Present' : 'Not found'}</dd></div>
            </dl>
          ) : report.adapter === 'powerpoint' && powerPoint ? (
            <dl>
              <div><dt>Slides</dt><dd>{powerPoint.slides}</dd></div>
              <div><dt>Hidden slides</dt><dd>{powerPoint.hiddenSlides}</dd></div>
              <div><dt>Words</dt><dd>{powerPoint.words.toLocaleString()}</dd></div>
              <div><dt>Charts</dt><dd>{powerPoint.charts}</dd></div>
              <div><dt>Images</dt><dd>{powerPoint.images}</dd></div>
              <div><dt>Speaker notes</dt><dd>{powerPoint.notesSlides}</dd></div>
            </dl>
          ) : report.adapter === 'pdf' && pdf ? (
            <dl>
              <div><dt>Pages</dt><dd>{pdf.pages.toLocaleString()}</dd></div>
              <div><dt>Readable pages</dt><dd>{pdf.readablePages.toLocaleString()}</dd></div>
              <div><dt>Words recovered</dt><dd>{pdf.words.toLocaleString()}</dd></div>
              <div><dt>Images</dt><dd>{pdf.images}</dd></div>
              <div><dt>Attachments</dt><dd>{pdf.attachments}</dd></div>
              <div><dt>Cross-reference</dt><dd>{pdf.xrefKind === 'table' ? 'Classic table' : pdf.xrefKind === 'stream' ? 'Xref stream' : 'Needs recovery'}</dd></div>
            </dl>
          ) : report.adapter === 'archive' && archive ? (
            <dl>
              <div><dt>Entries</dt><dd>{archive.entries.toLocaleString()}</dd></div>
              <div><dt>Intact entries</dt><dd>{archive.intactEntries.toLocaleString()}</dd></div>
              <div><dt>Corrupted</dt><dd>{archive.corruptedEntries.toLocaleString()}</dd></div>
              <div><dt>Encrypted</dt><dd>{archive.encryptedEntries.toLocaleString()}</dd></div>
              <div><dt>Recoverable bytes</dt><dd>{formatBytes(archive.recoveredBytes)}</dd></div>
              <div><dt>Directory</dt><dd>{archive.directoryStatus === 'valid' ? 'Valid' : archive.directoryStatus === 'missing' ? 'Missing' : 'Needs review'}</dd></div>
            </dl>
          ) : (
            <dl>
              <div><dt>Worksheets</dt><dd>{report.counts.worksheets}</dd></div>
              <div><dt>Cell values</dt><dd>{report.counts.cells.toLocaleString()}</dd></div>
              <div><dt>Formulas</dt><dd>{report.counts.formulas.toLocaleString()}</dd></div>
              <div><dt>Images</dt><dd>{report.counts.images}</dd></div>
              <div><dt>External links</dt><dd>{report.counts.externalLinks}</dd></div>
              <div><dt>VBA macros</dt><dd>{report.hasMacros ? 'Present' : 'Not found'}</dd></div>
            </dl>
          )}
        </section>
        <IssueList issues={report.issues} />
        <RepairPlan plan={report.repairPlan} />
      </div>
      <div className="analysis-actions">
        {report.capabilities.repair ? <Button variant="primary" full onClick={onRepair}>Start Safe Repair <ArrowRight /></Button> : null}
        {report.capabilities.extract ? <Button variant={report.capabilities.repair ? 'secondary' : 'primary'} full onClick={onExtract}><PackageOpen />Extract content</Button> : null}
        <Button variant="quiet" onClick={() => window.print()}><Download />Print summary</Button>
      </div>
      <p className="source-preservation">The original file is never overwritten. Every output receives a new filename.</p>
    </div>
  );
}
